import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Input } from "@/components/podkit/forms/Input";
import { Label } from "@/components/podkit/forms/Label";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { TrackLocations } from "@/hooks/use-segment";
import { useUpdateGroup } from "@/queries/group-queries";
import { formatError } from "@/utils/errors";
import { useCallback, useEffect, useState, type FC, type FormEvent } from "react";
import type { PlainMessage } from "@bufbuild/protobuf";
import type { Group } from "gitpod-next-api/gitpod/v1/group_pb";

type EditGroupDialogProps = {
    open: boolean;
    onClose: () => void;
    group: PlainMessage<Group>;
};

export const EditGroupDialog: FC<EditGroupDialogProps> = ({ open, onClose, group }) => {
    const { toast } = useToast();
    const updateGroup = useUpdateGroup();
    const [groupName, setGroupName] = useState(group.name);
    const [groupDescription, setGroupDescription] = useState(group.description || "");

    // Reset form when group changes or dialog opens
    useEffect(() => {
        if (open) {
            setGroupName(group.name);
            setGroupDescription(group.description || "");
        }
    }, [open, group.name, group.description]);

    const handleSubmit = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            if (!groupName.trim()) {
                return;
            }

            try {
                await updateGroup.mutateAsync({
                    groupId: group.id,
                    name: groupName.trim(),
                    description: groupDescription.trim() || undefined,
                });

                toast({
                    title: "Group updated",
                    description: `Group "${groupName}" has been updated successfully.`,
                });

                onClose();
            } catch (error) {
                toast({
                    title: "Failed to update group",
                    description: formatError(error),
                });
            }
        },
        [groupName, groupDescription, group.id, updateGroup, toast, onClose],
    );

    const handleOpenChange = useCallback(
        (open: boolean) => {
            if (!open) {
                onClose();
            }
        },
        [onClose],
    );

    const isFormValid = groupName.trim().length >= 3 && groupName.trim().length <= 80;
    const hasChanges = groupName !== group.name || groupDescription !== (group.description || "");

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent data-testid="edit-group-dialog" data-track-location={TrackLocations.CreateProjectModal}>
                <DialogHeader>
                    <DialogTitle>Edit group</DialogTitle>
                </DialogHeader>

                <DialogBody>
                    <form id="edit-group-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
                        <div className="flex flex-col gap-4">
                            <div className="flex flex-col gap-2">
                                <Label htmlFor="group-name" className="font-medium">
                                    Name
                                </Label>
                                <Input
                                    id="group-name"
                                    data-testid="group-name-input"
                                    name="groupName"
                                    value={groupName}
                                    onChange={(e) => setGroupName(e.target.value)}
                                    placeholder="Product managers"
                                    autoFocus
                                    required
                                    minLength={3}
                                    maxLength={80}
                                />
                            </div>
                            <div className="flex flex-col gap-2">
                                <Label htmlFor="group-description" className="font-medium">
                                    Description
                                </Label>
                                <Input
                                    id="group-description"
                                    data-testid="group-description-input"
                                    name="groupDescription"
                                    value={groupDescription}
                                    onChange={(e) => setGroupDescription(e.target.value)}
                                    placeholder="optional"
                                    maxLength={255}
                                />
                            </div>
                        </div>
                    </form>
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" data-testid="edit-group-dialog-cancel">
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        type="submit"
                        form="edit-group-form"
                        variant="primary"
                        disabled={!isFormValid || !hasChanges || updateGroup.isPending}
                        loading={updateGroup.isPending}
                        data-testid="edit-group-dialog-save"
                        data-tracking-id="edit-group-dialog-save"
                    >
                        Update
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
